import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../hooks/useSEO';

const HomePage = () => {
  useSEO({
    title: 'ホーム',
    description: '個人運営のFDM式3Dプリンター研究ノート。フィラメント設定データ、トラブルシューティング、実測データを記録・公開しています。',
    keywords: '3Dプリンター,FDM,フィラメント,PLA,PETG,ABS,トラブルシューティング,3D printing',
    canonicalPath: '/'
  });

  const categoryCards = [
    {
      title: 'フィラメントデータ',
      description: 'PLA、PETG、ABS等の設定値・実測データ',
      icon: 'ri-database-line',
      path: '/filament',
      accent: 'border-l-4 border-l-[#5f8d63]'
    },
    {
      title: 'トラブル対策',
      description: '反り、糸引き、層間剥離等の解決方法',
      icon: 'ri-tools-line',
      path: '/troubleshooting',
      accent: 'border-l-4 border-l-[#4c7ca6]'
    },
    {
      title: 'ブログ',
      description: '材料レビュー・設定メモ・実験記録',
      icon: 'ri-article-line',
      path: '/blog/index.php',
      accent: 'border-l-4 border-l-[#b56b3c]'
    },
    {
      title: '初心者向け',
      description: '3Dプリンターの基礎知識・購入前の準備',
      icon: 'ri-graduation-cap-line',
      path: '/beginner',
      accent: 'border-l-4 border-l-[#5f8d63]'
    },
    {
      title: '役立ち関連知識',
      description: '加工・後処理・CADソフト・工具紹介',
      icon: 'ri-lightbulb-line',
      path: '/knowledge',
      accent: 'border-l-4 border-l-[#4c7ca6]'
    }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        {/* Hero Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center space-y-8">
              {/* Logo and Main Title */}
              <div className="space-y-6">
                {/* Large Logo Display */}
                <div className="flex justify-center mb-6">
                  <img 
                    src="/assets/nag-logo-512.png" 
                    alt="せきそう NAG ノート ロゴ" 
                    className="w-32 h-32 object-contain drop-shadow-2xl"
                  />
                </div>
                
                <div className="plastic-logo inline-block">
                  <h1 className="text-4xl md:text-6xl font-bold tracking-wide">
                    せきそうNAGノート
                  </h1>
                </div>
                <div className="flex items-center justify-center space-x-4">
                  <div className="h-px bg-gradient-to-r from-transparent via-accent to-transparent w-32"></div>
                  <div className="tech-label">
                    FDM方式3Dプリンター技術記録庫
                  </div>
                  <div className="h-px bg-gradient-to-r from-transparent via-accent to-transparent w-32"></div>
                </div>
                <p className="text-metal-light text-lg font-['Roboto_Mono'] tracking-wide">
                  個人運営のFDM式3Dプリンター研究ノート
                </p>
              </div>

              {/* Introduction Text */}
              <div className="max-w-4xl mx-auto">
                <div className="static-card p-8 rivet-decoration">
                  <div className="space-y-6">
                    <p className="text-metal-light text-lg leading-relaxed">
                      このサイトは、個人的に運用している<br />
                      <strong className="text-white">FDM（熱溶解積層）方式3Dプリンター</strong>の記録ノートです。
                    </p>
                    <p className="text-metal-light leading-relaxed">
                      日々の出力条件や試験結果を整理しながら、<br />
                      各フィラメントの設定や傾向、トラブル対策などを少しずつまとめています。
                    </p>
                    <p className="text-metal-light leading-relaxed">
                      掲載している内容は、すべて実際の造形や測定に基づいたデータで、<br />
                      同じ環境での再現や改善の参考になればと思い記録しています。
                    </p>
                    <div className="pt-4">
                      <p className="text-accent text-sm font-['Roboto_Mono'] italic">
                        *Personal log of experiments and data for FDM 3D printing.*
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Cards Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white font-['Exo'] mb-4">
                記録カテゴリ
              </h2>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryCards.map((card, index) => {
                // ブログの場合は通常のaタグを使用
                if (card.path === '/blog/index.php') {
                  return (
                    <a
                      key={index}
                      href={card.path}
                      className={`clickable-card p-6 group ${card.accent}`}
                    >
                      <div className="space-y-4">
                        <div className="flex items-center space-x-4">
                          <div className="w-14 h-14 flex items-center justify-center bg-accent text-white border border-white/20">
                            <i className={`${card.icon} text-2xl`}></i>
                          </div>
                          <h3 className="text-xl font-semibold text-white font-['Exo'] group-hover:text-accent transition-colors">
                            {card.title}
                          </h3>
                        </div>
                        <p className="text-metal-light leading-relaxed pl-18">
                          {card.description}
                        </p>
                        <div className="flex items-center text-accent text-sm font-['Roboto_Mono'] group-hover:text-white transition-colors pt-2 pl-18">
                          <span>▶ 詳細を見る</span>
                          <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                        </div>
                      </div>
                    </a>
                  );
                }
                
                // その他のページはLinkコンポーネントを使用
                return (
                  <Link
                    key={index}
                    to={card.path}
                    className={`clickable-card p-6 group ${card.accent}`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 flex items-center justify-center bg-accent text-white border border-white/20">
                          <i className={`${card.icon} text-2xl`}></i>
                        </div>
                        <h3 className="text-xl font-semibold text-white font-['Exo'] group-hover:text-accent transition-colors">
                          {card.title}
                        </h3>
                      </div>
                      <p className="text-metal-light leading-relaxed pl-18">
                        {card.description}
                      </p>
                      <div className="flex items-center text-accent text-sm font-['Roboto_Mono'] group-hover:text-white transition-colors pt-2 pl-18">
                        <span>▶ 詳細を見る</span>
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default HomePage;
